/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import RemoveIcon from '@mui/icons-material/Remove';
import { Alert, CircularProgress } from '@mui/material';

import { AlignCenter } from '../../components/AlignCenter/AlignCenter';
import {
  HUMaterialTable,
  HUMaterialTableProps,
  iconCellStyles,
} from '../../components/HUMaterialTable/HUMaterialTable';
import { HUSpin } from '../../components/HUSpin/HUSpin';
import { SimpleConfirmModal } from '../../components/SimpleConfirmModal/SimpleConfirmModal';
import { SimpleNotifyModal } from '../../components/SimpleNotifyModal/SimpleNotifyModal';
import { Namespaces } from '../../i18n';
import { SuperuserClusterApiService } from '../../services/SuperuserClusterApiService';
import { ApiClientError } from '../../types/api/ApiClientError';
import { AddOrphanToWhitelistRequest } from '../../types/superuser/AddOrphanToWhitelistRequest';
import { DeleteOrphanFromWhitelistRequest } from '../../types/superuser/DeleteOrphanFromWhitelistRequest';
import { DeleteSuperuserClusterRequest } from '../../types/superuser/DeleteSuperuserClusterRequest';
import { OrphanClusterDetails } from '../../types/superuser/OrphanClusterDetails';
import { fetchOrphans } from '../../utils/queryFns';
import { reactToRemoveClusterFromAdminTable } from '../../utils/reactions';
import { searchSubstring } from '../../utils/searchSubstring';
import { useDocumentTitle } from '../../utils/useDocumentTitle';
import { useObjectState } from '../../utils/useObjectState';
import { useAdminOrphansPageStyles } from './AdminOrphansPage.styles';

type ModalState = {
  confirmModal: {
    isOpen: boolean;
    modalText: string;
    type: 'add' | 'delete' | 'remove' | '';
  };
  errorModal: {
    isOpen: boolean;
    modalText?: string;
  };
  successModal: {
    isOpen: boolean;
    modalText: string;
  };
};

export function AdminOrphansPage() {
  const { t } = useTranslation(Namespaces.Admin);
  const classes = useAdminOrphansPageStyles();
  const [modalState, setModalState] = useObjectState<ModalState>({
    confirmModal: {
      isOpen: false,
      modalText: '',
      type: '',
    },
    errorModal: {
      isOpen: false,
      modalText: '',
    },
    successModal: {
      isOpen: false,
      modalText: '',
    },
  });
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [rowData, setRowData] = useState<OrphanClusterDetails | undefined>(undefined);
  useDocumentTitle(t(`${Namespaces.Common}:page_titles.orphans`));

  const queryOrphans = useQuery(fetchOrphans.key, fetchOrphans.fn);

  const orphans = useMemo(() => queryOrphans.data?.payload ?? [], [queryOrphans]);

  const isOrphansLoading = useMemo(() => queryOrphans.status === 'loading', [queryOrphans.status]);

  const isError = useMemo(
    () => queryOrphans.error && queryOrphans.status === 'error',
    [queryOrphans.error, queryOrphans.status],
  );

  const confirmationModal = useCallback(
    (modalText: string, type: ModalState['confirmModal']['type']) => {
      setModalState({
        confirmModal: {
          isOpen: true,
          modalText,
          type,
        },
      });
    },
    [setModalState],
  );

  const onDeleteCluster = async (rowData: OrphanClusterDetails) => {
    setIsFetching(true);
    setModalState({
      confirmModal: {
        isOpen: false,
        modalText: '',
        type: '',
      },
    });
    try {
      const requestData: DeleteSuperuserClusterRequest = {
        workspaceId: rowData.workspaceId,
        customerId: rowData.customerId,
        clusterId: rowData.clusterId,
      };
      await SuperuserClusterApiService.deleteCluster({ data: requestData });
      await reactToRemoveClusterFromAdminTable();
      setModalState({
        successModal: {
          isOpen: true,
          modalText: t('clusters.modals.success_deleting'),
        },
      });
    } catch (e) {
      const error = e as ApiClientError;
      if (error.response) {
        const jsonErrorObject = JSON.stringify(error.response.data, null, 2);
        setModalState({
          errorModal: {
            isOpen: true,
            modalText: jsonErrorObject,
          },
        });
      } else {
        setModalState({
          errorModal: {
            isOpen: true,
          },
        });
      }
    } finally {
      setIsFetching(false);
    }
  };

  const onAddToWhitelist = async (rowData: OrphanClusterDetails) => {
    setIsFetching(true);
    setModalState({
      confirmModal: {
        isOpen: false,
        modalText: '',
        type: '',
      },
    });
    try {
      const requestData: AddOrphanToWhitelistRequest = {
        workspaceId: rowData.workspaceId,
        customerId: rowData.customerId,
        clusterId: rowData.clusterId,
      };
      await SuperuserClusterApiService.addOrphanToWhitelist({
        data: requestData,
      });
      await reactToRemoveClusterFromAdminTable();
      setModalState({
        successModal: {
          isOpen: true,
          modalText: t('clusters.modals.success_adding'),
        },
      });
    } catch (e) {
      const error = e as ApiClientError;
      if (error.response) {
        const jsonErrorObject = JSON.stringify(error.response.data, null, 2);
        setModalState({
          errorModal: {
            isOpen: true,
            modalText: jsonErrorObject,
          },
        });
      } else {
        setModalState({
          errorModal: {
            isOpen: true,
          },
        });
      }
    } finally {
      setIsFetching(false);
    }
  };

  const onRemoveFromWhitelist = async (rowData: OrphanClusterDetails) => {
    setIsFetching(true);
    setModalState({
      confirmModal: {
        isOpen: false,
        modalText: '',
        type: '',
      },
    });
    try {
      const requestData: DeleteOrphanFromWhitelistRequest = {
        workspaceId: rowData.workspaceId,
        customerId: rowData.customerId,
        clusterId: rowData.clusterId,
      };
      await SuperuserClusterApiService.deleteOrphanFromWhitelist({
        data: requestData,
      });
      await reactToRemoveClusterFromAdminTable();
      setModalState({
        successModal: {
          isOpen: true,
          modalText: t('clusters.modals.success_removing'),
        },
      });
    } catch (e) {
      const error = e as ApiClientError;
      if (error.response) {
        const jsonErrorObject = JSON.stringify(error.response.data, null, 2);
        setModalState({
          errorModal: {
            isOpen: true,
            modalText: jsonErrorObject,
          },
        });
      } else {
        setModalState({
          errorModal: {
            isOpen: true,
          },
        });
      }
    } finally {
      setIsFetching(false);
    }
  };

  const onConfirm = (type: ModalState['confirmModal']['type']) => {
    type === 'delete'
      ? void onDeleteCluster(rowData!)
      : type === 'add'
        ? void onAddToWhitelist(rowData!)
        : void onRemoveFromWhitelist(rowData!);
  };

  const handleAddRemoveDeleteOrphan = useCallback(
    (modalText: string, type: 'add' | 'delete' | 'remove') =>
      (_: unknown, rowData: OrphanClusterDetails | OrphanClusterDetails[]) => {
        const data = Array.isArray(rowData) ? rowData[0] : rowData;
        setRowData(data);
        confirmationModal(modalText, type);
      },
    [confirmationModal],
  );

  const getStatusIcon = useCallback(
    (status: boolean | undefined) =>
      status ? <CheckCircleIcon className={classes.success} /> : <CancelIcon className={classes.failure} />,
    [classes.success, classes.failure],
  );

  const tableConfig = useMemo<HUMaterialTableProps<OrphanClusterDetails>>(
    () => ({
      columns: [
        {
          title: t('clusters.table.labels.namespace'),
          field: 'workspaceId',
          render: rowData => `${rowData.workspaceId}-${rowData.customerId}-${rowData.clusterId}`,
          customFilterAndSearch: (term, rowData) =>
            searchSubstring(term, `${rowData.workspaceId}-${rowData.customerId}-${rowData.clusterId}`),
        },
        {
          title: t('clusters.table.labels.status'),
          field: 'status',
        },
        {
          title: t('clusters.table.labels.whitelisted'),
          field: 'whitelisted',
          render: rowData => getStatusIcon(rowData.whitelisted),
          cellStyle: iconCellStyles,
          headerStyle: iconCellStyles,
        },
      ],
      title: t('clusters.orphans_title'),
      actions: [
        rowData => {
          if (rowData.whitelisted) {
            return {
              icon: () => <RemoveIcon />,
              tooltip: t('clusters.table.actions.remove_from_whitelist'),
              disabled: isFetching,
              onClick: handleAddRemoveDeleteOrphan(t('clusters.modals.confirm_removing_from_whitelist'), 'remove'),
            };
          }
          return {
            icon: () => <AddIcon />,
            tooltip: t('clusters.table.actions.add_to_whitelist'),
            disabled: isFetching,
            onClick: handleAddRemoveDeleteOrphan(t('clusters.modals.confirm_adding_to_whitelist'), 'add'),
          };
        },
        {
          icon: () => <DeleteIcon />,
          tooltip: t('clusters.table.actions.delete_cluster'),
          disabled: isFetching,
          onClick: handleAddRemoveDeleteOrphan(t('clusters.modals.confirm_deleting'), 'delete'),
        },
      ],
      data: orphans,
      options: {
        actionsColumnIndex: -1,
        draggable: false,
        sorting: false,
        search: true,
      },
    }),
    [getStatusIcon, handleAddRemoveDeleteOrphan, isFetching, orphans, t],
  );

  return isOrphansLoading ? (
    <AlignCenter>
      <CircularProgress color="primary" size={40} />
    </AlignCenter>
  ) : isError ? (
    <Alert severity="error">{t(`${Namespaces.Common}:errors.unexpected_error`)}</Alert>
  ) : (
    <HUSpin spinning={isFetching} delay={50}>
      <HUMaterialTable {...tableConfig} />
      <SimpleConfirmModal
        open={modalState.confirmModal.isOpen}
        message={modalState.confirmModal.modalText}
        onCancel={() => {
          setModalState({
            confirmModal: {
              isOpen: false,
              modalText: '',
              type: '',
            },
          });
          setRowData(undefined);
        }}
        onConfirm={() => onConfirm(modalState.confirmModal.type)}
      />
      <SimpleNotifyModal
        open={modalState.errorModal.isOpen}
        message={
          modalState.errorModal.modalText ? (
            <pre className={classes.modalError}>{modalState.errorModal.modalText}</pre>
          ) : (
            t(`${Namespaces.Common}:errors.unexpected_error`)
          )
        }
        onOk={() => setModalState({ errorModal: { isOpen: false, modalText: '' } })}
      />
      <SimpleNotifyModal
        open={modalState.successModal.isOpen}
        message={modalState.successModal.modalText}
        onOk={() => setModalState({ successModal: { isOpen: false, modalText: '' } })}
      />
    </HUSpin>
  );
}
